import { useState, type CSSProperties } from 'react';
import { useNotifications } from '@/application/usecases/useNotifications';
import { strings } from '@/shared/strings';
import { PauseIcon, PlayIcon } from '@/ui/icons';
import { useOpenNotification } from './useOpenNotification';
import styles from './NotificationTicker.module.css';

const text = strings.notifications.ticker;

/** The seconds one letter takes to cross, so the line reads at one pace however much it carries. */
const SECONDS_A_LETTER = 0.16;
/** The least a full turn takes: one short notice does not race across. */
const SHORTEST_TURN = 18;
/** The most the line carries. More than this is what the bell and the centre are for. */
const MOST = 30;

/**
 * The important notifications, on one line that moves across the screen as a
 * news line does. The request of 8 October 2026 (fourth).
 *
 * Important is what management marked Mandatory for the person's role, or for
 * the person, in Users: the line carries those, while they are unread, and
 * nothing else. It draws nothing at all when there are none.
 *
 * Each notice is a button: it opens its case and is marked read, exactly as a
 * row of the centre does, and then it has left the line. The line stops while
 * the pointer is over it or the keyboard is in it, and the button at its end
 * stops it for a finger. A device that asks for less motion gets the same
 * notices standing still, to be scrolled sideways.
 *
 * The line is drawn twice so that it has no end; the second drawing is for the
 * eye alone. Its words are the stylesheet's, read from the element, so a
 * screen reader hears each notice once and it can still be pressed.
 */
export function NotificationTicker() {
  const { data } = useNotifications({ important: true, unread: true, pageSize: MOST });
  const open = useOpenNotification();
  const [paused, setPaused] = useState(false);

  const rows = data ?? [];
  if (rows.length === 0) return null;

  const letters = rows.reduce((sum, row) => sum + row.title.length + row.text.length + 6, 0);
  const turn = Math.max(SHORTEST_TURN, Math.round(letters * SECONDS_A_LETTER));
  const style = { '--ticker-turn': `${String(turn)}s` } as CSSProperties;

  return (
    <section className={styles.ticker} aria-label={text.label} data-paused={paused || undefined}>
      <span className={styles.badge}>
        {text.badge}
        <span className={styles.count}>{rows.length}</span>
      </span>

      <div className={styles.window}>
        <div className={styles.track} style={style}>
          <span className={styles.group}>
            {rows.map((row) => (
              <button key={row.id} type="button" className={styles.item} onClick={() => open(row)}>
                <b>{row.title}</b>
                <span>{row.text}</span>
              </button>
            ))}
          </span>
          <span className={styles.group} aria-hidden="true">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                className={`${styles.item} ${styles.again}`}
                tabIndex={-1}
                data-title={row.title}
                data-text={row.text}
                onClick={() => open(row)}
              />
            ))}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={styles.toggle}
        aria-pressed={paused}
        aria-label={text.pause}
        title={text.pause}
        onClick={() => setPaused((now) => !now)}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </button>
    </section>
  );
}
